import { motion } from "framer-motion"
import { formatDate, isSameDay } from "lopermut"
import { InfoIcon, RadarIcon } from "lucide-react"
import { ReactNode, useEffect, useMemo, useRef } from "react"
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../atoms/Tooltip"
import { CardContent } from "../../molecules/Card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../molecules/Chart"
import { cn } from "../../utils"

const ACTIVITY_BAR_SIZE = 20
const STACK_PREFIX = "__stack_"

export type ComparisonSeriesType = "bar" | "line"
export type ComparisonValueType = "absolute" | "rate" | "secondary"
export type StackValueMode = "cumulative" | "segments"

/** Bar keys should be listed outermost (total) to innermost. Lines can appear anywhere. */
export type ComparisonChartConfig = {
  [key: string]: ChartConfig[string] & {
    type: ComparisonSeriesType
    valueType?: ComparisonValueType
  }
}

export type ComparisonDatum = {
  date?: string | null
  opacity?: number
} & Record<string, number | string | null | undefined>

type Props = {
  data?: ComparisonDatum[]
  chartConfig: ComparisonChartConfig
  isPending: boolean
  noDataT?: {
    title: string
    description: string
  }
  action?: ReactNode
  chartClassName?: string
  focus?: string
  /** "segments" shows stacked diffs in the tooltip; "cumulative" shows nested totals. */
  stackValueMode?: StackValueMode
  legendTooltip?: string
  legendTooltipByKey?: Partial<Record<string, string>>
  secondaryMaxValue?: number
}

export const ComparisonActivityChart = ({
  data,
  chartConfig,
  isPending,
  noDataT,
  action,
  chartClassName,
  focus,
  stackValueMode = "cumulative",
  legendTooltip,
  legendTooltipByKey,
  secondaryMaxValue,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const noData = !isPending && !data?.length
  const barKeys = useMemo(() => getSeriesKeys(chartConfig, "bar"), [chartConfig])
  const lineKeys = useMemo(() => getSeriesKeys(chartConfig, "line"), [chartConfig])
  const visibleBarKeys = useMemo(
    () => barKeys.filter((key) => hasValue(data, key)),
    [barKeys, data],
  )
  const visibleLineKeys = useMemo(
    () => lineKeys.filter((key) => hasValue(data, key)),
    [lineKeys, data],
  )
  const stackedBarKeys = useMemo(() => [...visibleBarKeys].reverse(), [visibleBarKeys])

  const dateFormat = useMemo(() => {
    const dates = data?.map((item) => item.date).filter((date) => Boolean(date)) ?? []
    const hasSubDayBuckets = dates.some(
      (date, index) => index > 0 && isSameDay(new Date(date!), new Date(dates[index - 1]!)),
    )

    return hasSubDayBuckets ? "MMM d, HH:mm" : "MMM d"
  }, [data])

  const chartData = useMemo((): ComparisonDatum[] | undefined => {
    return data?.map((item) => {
      let formattedDate = null

      try {
        formattedDate = item.date ? formatDate(item.date, dateFormat) : null
      } catch (error) {
        console.error(error)
      }

      const point: ComparisonDatum = {
        ...item,
        ...computeStacks(item, visibleBarKeys),
        date: formattedDate,
        opacity: item.opacity ?? 1,
      }

      return point
    })
  }, [data, dateFormat, visibleBarKeys])

  const renderOpacityCells = () =>
    chartData?.map((item, index) => <Cell key={index} fillOpacity={item.opacity ?? 1} />)

  const maxValue = useMemo(() => {
    if (!chartData?.length) return 0

    return Math.round(
      Math.max(
        ...chartData.map((item) => {
          const barValues = visibleBarKeys.map((key) => asNumber(item[key]) ?? 0)
          const absoluteLineValues = visibleLineKeys
            .filter((key) => (chartConfig[key]?.valueType ?? "rate") === "absolute")
            .map((key) => asNumber(item[key]) ?? 0)

          return Math.max(0, ...barValues, ...absoluteLineValues) * 1.1
        }),
      ),
    )
  }, [chartData, chartConfig, visibleBarKeys, visibleLineKeys])

  const calculatedSecondaryMaxValue = useMemo(() => {
    if (!chartData?.length) return 1

    const secondaryLineKeys = visibleLineKeys.filter(
      (key) => chartConfig[key]?.valueType === "secondary",
    )
    const max = Math.max(
      0,
      ...chartData.flatMap((item) =>
        secondaryLineKeys.map((key) => asNumber(item[key]) ?? 0),
      ),
    )

    return max > 0 ? max * 1.1 : 1
  }, [chartData, chartConfig, visibleLineKeys])
  const resolvedSecondaryMaxValue = secondaryMaxValue ?? calculatedSecondaryMaxValue

  useEffect(() => {
    if (containerRef.current && chartData?.length) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth
    }
  }, [chartData])

  return (
    <div className="flex flex-col w-full relative rounded-none border-none gap-2">
      <div
        className={cn(
          "flex flex-row items-end justify-between gap-4 pr-4",
          !action && "justify-end",
        )}
      >
        {action}
        <ActivityTooltipInfo
          className={cn(noData && "pointer-events-none grayscale opacity-15")}
          chartConfig={chartConfig}
          visibleBarKeys={visibleBarKeys}
          visibleLineKeys={visibleLineKeys}
          focus={focus}
          legendTooltip={legendTooltip}
          legendTooltipByKey={legendTooltipByKey}
        />
      </div>
      {noData && (
        <div className="flex items-center justify-center absolute inset-0 z-10">
          <div className="flex flex-col gap-2 py-14 items-center justify-center text-center m-auto max-w-sm">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <RadarIcon className="size-10 text-muted-foreground mb-3" strokeWidth={1.5} />
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <h2 className="text-md text-foreground text-stroke">{noDataT?.title}</h2>
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <p className="text-sm text-muted-foreground text-stroke-sm">{noDataT?.description}</p>
            </motion.div>
          </div>
        </div>
      )}
      <CardContent
        className={cn(
          "flex flex-col p-0 gap-2 items-end",
          noData && "pointer-events-none grayscale opacity-15",
        )}
      >
        <div ref={containerRef} className="w-full overflow-x-auto pb-2 -mb-2">
          <ChartContainer
            config={chartConfig}
            className={cn("aspect-auto h-[180px]", chartClassName)}
            style={{
              width: `${(chartData?.length ?? 0) * ACTIVITY_BAR_SIZE}px`,
              minWidth: "100%",
            }}
          >
            <ComposedChart accessibilityLayer data={chartData} margin={{ top: 5, bottom: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={30}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                width={30}
                domain={[0, maxValue]}
                allowDataOverflow
                hide
              />
              <YAxis
                yAxisId="percentage"
                orientation="right"
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                width={30}
                domain={[0, 100]}
                allowDataOverflow
                hide
              />
              <YAxis
                yAxisId="secondary"
                orientation="right"
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                width={30}
                domain={[0, resolvedSecondaryMaxValue]}
                allowDataOverflow
                hide
              />
              <ChartTooltip
                content={(props: TooltipContentProps) => {
                  const seen = new Set<string>()
                  const normalizedPayload = props.payload?.flatMap((item) => {
                    const key = seriesKeyFromDataKey(item.dataKey)
                    const series = chartConfig[key]

                    if (!series || seen.has(key)) return []

                    seen.add(key)

                    const nestedValue = asNumber(item.payload?.[key])
                    const segmentValue = asNumber(item.payload?.[stackKey(key)])
                    const rawValue =
                      series.type === "bar" && stackValueMode === "segments"
                        ? segmentValue ?? nestedValue
                        : nestedValue
                    const displayValue = rawValue ?? item.value
                    const value = isRateSeries(chartConfig, key) ? `${displayValue}%` : displayValue

                    return [
                      {
                        ...item,
                        name: key,
                        dataKey: key,
                        value,
                        color: getColor(key, chartConfig, focus),
                      },
                    ]
                  })

                  return (
                    <ChartTooltipContent
                      active={props.active}
                      payload={normalizedPayload}
                      label={props.label}
                      coordinate={props.coordinate}
                      payloadOrder={Object.keys(chartConfig)}
                    />
                  )
                }}
              />
              {stackedBarKeys.map((key) => (
                <Bar
                  key={key}
                  dataKey={stackKey(key)}
                  stackId="a"
                  fill={getColor(key, chartConfig, focus)}
                  barSize={ACTIVITY_BAR_SIZE}
                  className="transition-all"
                >
                  {renderOpacityCells()}
                </Bar>
              ))}
              {visibleLineKeys.map((key) => {
                const isRate = isRateSeries(chartConfig, key)
                const yAxisId = getLineYAxisId(chartConfig, key)

                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    yAxisId={yAxisId}
                    strokeDasharray={isRate ? "2 2" : undefined}
                    stroke={getColor(key, chartConfig, focus)}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                    className="transition-all"
                    connectNulls
                  />
                )
              })}
            </ComposedChart>
          </ChartContainer>
        </div>
      </CardContent>
    </div>
  )
}

const ActivityTooltipInfo = ({
  className,
  chartConfig,
  visibleBarKeys,
  visibleLineKeys,
  focus,
  legendTooltip,
  legendTooltipByKey,
}: {
  className?: string
  chartConfig: ComparisonChartConfig
  visibleBarKeys: string[]
  visibleLineKeys: string[]
  focus?: string
  legendTooltip?: string
  legendTooltipByKey?: Partial<Record<string, string>>
}) => {
  return (
    <div className={cn("flex items-center justify-end gap-4 flex-wrap", className)}>
      {Object.entries(chartConfig).map(([key, config]) => {
        if (config.type === "line" && !visibleLineKeys.includes(key)) return null
        if (config.type === "bar" && visibleBarKeys.length > 0 && !visibleBarKeys.includes(key)) {
          return null
        }

        const tooltip = legendTooltipByKey?.[key]

        return (
          <div key={key} className="flex items-center gap-1.5">
            <div
              className={cn(
                "size-2 rounded-full",
                !focus || focus === key ? "opacity-100" : "opacity-50",
              )}
              style={{
                background: getColor(key, chartConfig, focus),
              }}
            />
            <span className="text-sm text-muted-foreground">{config.label}</span>
            {tooltip && (
              <Tooltip>
                <TooltipTrigger className="inline-flex text-muted-foreground">
                  <InfoIcon className="size-3.5" />
                </TooltipTrigger>
                <TooltipContent className="max-w-90 text-left">{tooltip}</TooltipContent>
              </Tooltip>
            )}
          </div>
        )
      })}
      {legendTooltip && (
        <Tooltip>
          <TooltipTrigger className="inline-flex text-muted-foreground">
            <InfoIcon className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent className="max-w-90 text-left">{legendTooltip}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

function asNumber(value: unknown) {
  return typeof value === "number" && !Number.isNaN(value) ? value : undefined
}

function stackKey(key: string) {
  return `${STACK_PREFIX}${key}`
}

function seriesKeyFromDataKey(dataKey: unknown) {
  const key = String(dataKey ?? "")

  return key.startsWith(STACK_PREFIX) ? key.slice(STACK_PREFIX.length) : key
}

function getSeriesKeys(chartConfig: ComparisonChartConfig, type: ComparisonSeriesType) {
  return Object.keys(chartConfig).filter((key) => chartConfig[key]?.type === type)
}

function isRateSeries(chartConfig: ComparisonChartConfig, key: string) {
  const series = chartConfig[key]

  if (!series || series.type !== "line") return false

  return (series.valueType ?? "rate") === "rate"
}

function getLineYAxisId(chartConfig: ComparisonChartConfig, key: string) {
  const valueType = chartConfig[key]?.valueType ?? "rate"

  if (valueType === "rate") return "percentage"
  if (valueType === "secondary") return "secondary"

  return undefined
}

function getColor(key: string, chartConfig: ComparisonChartConfig, focus?: string) {
  if (focus) {
    if (focus === key) return "var(--chart-primary)"

    return "var(--chart-secondary)"
  }

  return chartConfig[key]?.color ?? "var(--chart-primary)"
}

function hasValue(data: ComparisonDatum[] | undefined, key: string) {
  return data?.some((item) => asNumber(item[key]) != null) ?? false
}

function computeStacks(item: ComparisonDatum, barKeys: string[]) {
  const presentKeys = barKeys.filter((key) => asNumber(item[key]) != null)
  const innerToOuter = [...presentKeys].reverse()
  const diffs = innerToOuter.map((key, index) => {
    const value = asNumber(item[key]) ?? 0
    const previousKey = innerToOuter[index - 1]

    if (!previousKey) return value

    const innerValue = asNumber(item[previousKey]) ?? 0

    return value - innerValue
  })

  return Object.fromEntries(innerToOuter.map((key, index) => [stackKey(key), diffs[index]]))
}
